// System variables a criterion can be compared against (queryOption 'F' +
// `customValue` = token). The engine resolves them server-side at run time —
// they never prompt the user and never appear in the dashboard param bindings.
//
// The vocabulary mirrors the backend's closed `?.@Token` set:
// `bi_sql/placeholders.build_system_token_resolver` (Today / Now / login user /
// email) + `twasta_runtime_engine/bi_calendar_tokens.py` (the calendar date
// tokens; financial year = Apr 1 – Mar 31). Token names are matched
// case-insensitively; keep them EXACTLY resolvable by that resolver.

export interface SystemVariable {
  /** The token stored in the rule's `customValue` (no `?.@` prefix). */
  token: string
  /** Human label shown in the picker. */
  label: string
  group: 'Dates' | 'Current user'
}

export const SYSTEM_VARIABLES: SystemVariable[] = [
  { token: 'Today', label: 'Today', group: 'Dates' },
  { token: 'Now', label: 'Now (date + time)', group: 'Dates' },
  { token: '1stDayOfMonth', label: 'Start of current month', group: 'Dates' },
  { token: 'LastDayOfMonth', label: 'End of current month', group: 'Dates' },
  { token: '1stDateOfLastMonth', label: 'Start of previous month', group: 'Dates' },
  { token: 'LastDayOfLastMonth', label: 'End of previous month', group: 'Dates' },
  { token: '1stDayOfWeek', label: 'Start of current week (Mon)', group: 'Dates' },
  { token: 'LastDayOfWeek', label: 'End of current week (Sun)', group: 'Dates' },
  { token: '1stDateOfLastWeek', label: 'Start of previous week', group: 'Dates' },
  { token: 'LastDayOfLastWeek', label: 'End of previous week', group: 'Dates' },
  { token: '1stDayOfYear', label: 'Start of current year', group: 'Dates' },
  { token: 'LastDayOfYear', label: 'End of current year', group: 'Dates' },
  { token: '1stDateOfLastYear', label: 'Start of previous year', group: 'Dates' },
  { token: 'LastDayOfLastYear', label: 'End of previous year', group: 'Dates' },
  { token: '1stDayOfFinYear', label: 'Start of financial year (Apr 1)', group: 'Dates' },
  { token: 'LastDayOfFinYear', label: 'End of financial year (Mar 31)', group: 'Dates' },
  { token: '1stDateOfLastFinYear', label: 'Start of previous financial year', group: 'Dates' },
  { token: 'LastDayOfLastFinYear', label: 'End of previous financial year', group: 'Dates' },
  { token: 'Last1Month', label: '1 month back (month start)', group: 'Dates' },
  { token: 'Last3Month', label: '3 months back (month start)', group: 'Dates' },
  { token: 'Last6Month', label: '6 months back (month start)', group: 'Dates' },
  { token: 'Last12Month', label: '12 months back (month start)', group: 'Dates' },
  { token: 'LoginUser', label: 'Logged-in user (login id)', group: 'Current user' },
  { token: 'User_Email', label: 'Logged-in user email', group: 'Current user' },
]

/** How a criterion's value is compared: a run-time user prompt, a fixed
 *  literal baked into the model, or a server-resolved system variable. */
export type CompareWith = 'prompt' | 'literal' | 'variable'

/** Sentinel `customValue` meaning "not a system variable" (insight-bi legacy). */
export const FIXED_VALUE = 'FixedValue'

export function compareWithOf(rule: {
  queryOption?: string
  customValue?: string
}): CompareWith {
  if ((rule.queryOption || 'P').trim().toUpperCase() === 'P') return 'prompt'
  const cv = (rule.customValue || '').trim()
  if (cv && cv.toLowerCase() !== FIXED_VALUE.toLowerCase()) return 'variable'
  return 'literal'
}

export function systemVariableLabel(token: string): string {
  const t = (token || '').trim().toLowerCase()
  const found = SYSTEM_VARIABLES.find((v) => v.token.toLowerCase() === t)
  return found ? found.label : token
}
